import { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Input, Badge } from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";

const hour = (value) => String(value || "").slice(0, 5);

/** Una orden: su destino, el select de centros y el detalle del elegido. */
const OfficeRow = ({ orderId, reason, value, onChange }) => {
  const { getData } = useDataFetch();
  const { data, isLoading, error } = getData(
    ["interrapidisimo-offices", orderId],
    `/order/${orderId}/interrapidisimo/offices`
  );
  const offices = data?.data?.offices || [];
  const destination = data?.data?.destination;
  const office = offices.find((o) => o.id === value);

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex justify-content-between flex-wrap gap-2 mb-1">
        <strong>Orden {orderId.slice(-8)}</strong>
        {destination && (
          <span className="small text-muted">
            Cliente: {destination.address}, {destination.city}
          </span>
        )}
      </div>
      {reason && <div className="small text-danger mb-2">{reason}</div>}

      {isLoading && (
        <div className="small">
          <Spinner size="sm" /> Buscando centros de servicio...
        </div>
      )}
      {error && <Alert color="danger" className="mb-0">No se pudieron cargar los centros: {error.message}</Alert>}
      {!isLoading && !error && offices.length === 0 && (
        <Alert color="info" className="mb-0">No hay centros de servicio activos en la ciudad de destino.</Alert>
      )}

      {offices.length > 0 && (
        <Input
          type="select"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        >
          <option value="">Selecciona un centro de servicio...</option>
          {offices.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.type ? ` (${o.type})` : ""}
              {o.claim_in_office ? " · Reclame en oficina" : ""}
            </option>
          ))}
        </Input>
      )}

      {office && (
        <div className="small mt-2">
          <div className="text-muted">{office.name}</div>
          <div>Dirección: {office.address}</div>
          <div>
            IdLocalidad: {office.locality_id}
            {office.locality ? ` (${office.locality})` : ""}
          </div>
          {office.phone && <div>Tel: {office.phone}</div>}
          {office.claim_in_office && <Badge color="success">Reclame en oficina</Badge>}
          {office.schedule?.length > 0 && (
            <div className="mt-1">
              {office.schedule.map((s) => `${s.day} ${hour(s.opens)}-${hour(s.closes)}`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Interrapidísimo rechaza la entrega en dirección en zonas de difícil acceso.
 * Por cada orden afectada se elige el centro de servicio donde el cliente
 * reclamará el pedido; onConfirm recibe { [orderId]: IdCentroServicio }.
 *
 * @param orders [{ orderId, reason }]
 */
export const InterrapidisimoOfficeModal = ({ orders, toggle, onConfirm, isShipping }) => {
  const [selection, setSelection] = useState({});
  const chosen = Object.fromEntries(Object.entries(selection).filter(([, id]) => id));
  const pending = orders.length - Object.keys(chosen).length;

  return (
    <Modal isOpen={orders.length > 0} toggle={toggle} size="lg" centered backdrop="static" scrollable>
      <ModalHeader toggle={toggle}>Despachar a oficina de Interrapidísimo</ModalHeader>
      <ModalBody>
        <Alert color="warning">
          {orders.length === 1
            ? "Interrapidísimo no entrega en la dirección de esta orden."
            : `Interrapidísimo no entrega en la dirección de ${orders.length} órdenes.`}{" "}
          Elige el centro de servicio donde el cliente reclamará el pedido.
        </Alert>
        {orders.map(({ orderId, reason }) => (
          <OfficeRow
            key={orderId}
            orderId={orderId}
            reason={reason}
            value={selection[orderId]}
            onChange={(id) => setSelection((prev) => ({ ...prev, [orderId]: id }))}
          />
        ))}
      </ModalBody>
      <ModalFooter>
        {orders.length > 1 && pending > 0 && (
          <span className="small text-muted me-auto">
            Las órdenes sin centro se quedan pendientes de despacho.
          </span>
        )}
        <Button color="light" onClick={toggle} disabled={isShipping}>
          Cancelar
        </Button>
        <Button color="primary" onClick={() => onConfirm(chosen)} disabled={!Object.keys(chosen).length || isShipping}>
          {isShipping && <Spinner size="sm" className="me-2" />}
          Despachar {Object.keys(chosen).length > 1 ? `${Object.keys(chosen).length} órdenes` : "a oficina"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
